import { styled } from "goober";
import { PressButton } from "../PressButton";
import { Button } from "../Button";
import { ClickButton } from "../ClickButton";
import { InputContainer, WORKFLOW_INPUT_HEIGHT } from "../InputContainer";
import { Link } from "../Link";

export const Container = styled("div")`
  display: flex;
  flex: 1;
  height: 0;
  flex-direction: column;
  position: relative;

  &[data-outer-scroll="true"] {
    flex: none;
    height: auto;
    min-height: 100%;
  }
`;

export const Messages = styled("div")`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 24px 56px 12px;
  margin-right: -12px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  > :nth-child(1) {
    margin-top: auto;
  }

  &[data-outer-scroll="true"] {
    height: auto;
    overflow-y: visible;
    overflow-x: visible;
    margin-right: 0;
  }
  &[data-outer-scroll="true"] > :nth-child(1) {
    margin-top: 0;
  }
`;

export const ChatForm = styled("form")`
  display: flex;
  gap: 8px;
  padding: 0 4px 4px;

  &[data-outer-scroll="true"] {
    position: sticky;
    bottom: 0;
    background: var(--surface-primary);
    z-index: 1;
  }
`;

export const TextareaContainer = styled("div")`
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
  position: relative;
  isolation: isolate;
`;

export const ScrollToBottomButton = styled(PressButton)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, calc(-100% - 12px));
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-primary);
  &:hover {
    background: var(--surface-secondary);
  }
`;

export const StatusContainer = styled("div")`
  display: flex;
  gap: 6px;
  align-items: center;
  max-width: 100%;
  padding: 8px;
  border-radius: 12px;

  &[data-clickable="true"] {
    cursor: pointer;
  }
  &[data-clickable="true"]:hover {
    background: var(--alpha2);
  }
  &[data-follow-active="true"] {
    background: var(--alpha2);
    box-shadow: 0 0 0 1px var(--border-alpha) inset;
  }
`;

export const FollowAvatarButton = styled(ClickButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: inherit;
`;

export const RecordingIndicator = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  margin-top: -4px;
  margin-bottom: -4px;
  padding: 4px 6px;
  background: var(--alpha4);
  border-radius: 50px;
`;

export const RecordingDot = styled("div")`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--red-600);
`;

export const VoiceRecorderContainer = styled(InputContainer)`
  height: ${WORKFLOW_INPUT_HEIGHT}px;
  box-sizing: border-box;
  padding: 0;

  > canvas {
    position: absolute;
    top: 12px;
    left: 12px;
    width: calc(100% - 24px) !important;
    height: calc(100% - 48px) !important;
  }
`;

export const ComposerInputWrap = styled("div")`
  position: relative;
  &::after {
    content: "";
    position: absolute;
    left: 0.5px;
    right: 0.5px;
    bottom: 0.5px;
    height: 48px;
    z-index: 1;
    pointer-events: none;
    border-bottom-left-radius: 11.5px;
    border-bottom-right-radius: 11.5px;
    background: linear-gradient(
      to bottom,
      transparent,
      var(--surface-primary) 8px,
      var(--surface-primary)
    );
    opacity: 0;
  }
  &[data-show-fade="true"]::after {
    opacity: 1;
  }
`;

export const ComposerControls = styled("div")`
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
`;

export const ComposerControlGroup = styled("div")`
  display: flex;
  gap: 4px;
  align-items: center;
  pointer-events: auto;
`;

const checkpointLinkBase = `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 38px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  color: var(--text-secondary);
  text-decoration: none;
  opacity: 0;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    background 0.15s;
  & > span:first-child {
    transition: transform 0.4s ease;
  }
  &[data-visible="true"] {
    opacity: 1;
    transform: translateY(0);
  }
  &:hover {
    background: var(--surface-secondary);
  }
`;

export const PrevCheckpointLink = styled(Link)`
  ${checkpointLinkBase}
  margin: 12px auto 4px;
  transform: translateY(-12px);
  &:hover > span:first-child {
    transform: rotate(-360deg);
  }
`;

export const NextCheckpointLink = styled(Link)`
  ${checkpointLinkBase}
  margin: 20px auto 20px;
  transform: translateY(12px);
  &[data-visible="true"] {
    opacity: 1;
    transform: translateY(0);
    transition-delay: 50ms;
  }
  &:hover > span:first-child {
    transform: rotate(360deg);
  }
`;

export const StopButton = styled(Button)`
  &.secondary {
    background: var(--alpha4);
    &:hover:not(:disabled) {
      background: var(--alpha8);
    }
  }
`;
